/*
  Title Balancer for collection and category cards
  - Ensures titles under images scale uniformly per section
  - Allows up to 2 lines; picks the smallest scale needed across siblings
  - Responsive across viewport sizes (recomputes on resize)
*/
(function () {
  const MAX_LINES = 2;
  const MIN_SCALE = 0.72; // don't get excessively small
  const PRODUCT_KEY_PREFIX = 'globalProductTitleScale.v1.'; // per-breakpoint key

  // Remove any stray Liquid trim tokens that might have leaked into the DOM
  // Example: a visible string like "-%}" under Seasonal Collections.
  function cleanupStrayTokens(root) {
    if (!root) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const candidates = [];
    const isStray = (t) => {
      if (!t) return false;
      const s = t.trim();
      // Only remove very specific, obviously stray tokens
      return s === '-%}' || s === '%}' || s === '{%-' || /^-\s*%}$/.test(s);
    };
    let node;
    while ((node = walker.nextNode())) {
      if (isStray(node.textContent)) candidates.push(node);
    }
    for (const n of candidates) {
      if (n && n.parentNode) n.parentNode.removeChild(n);
    }
  }

  function clampScale(n) {
    if (!isFinite(n) || n <= 0) return 1;
    return Math.max(MIN_SCALE, Math.min(1, n));
  }

  function measureRequiredScale(el) {
    // Prefer the anchor for width/height measurement since it wraps the text + icon
    const node = el.querySelector('a') || el;
    if (!node || !node.clientWidth || el.offsetParent === null) return 1;

    const cs = window.getComputedStyle(node);
    const lh = parseFloat(cs.lineHeight) || (parseFloat(cs.fontSize) * 1.2) || 16 * 1.2;
    // If an arrow icon is present inside the heading link, enforce a single-line fit
    // so the arrow stays on the same line as the title.
  const hasArrow = !!node.querySelector('.icon-wrap');
  const desiredLines = hasArrow ? 1 : MAX_LINES;
    const allowedHeight = lh * desiredLines;

    // scrollHeight/Width at the current (baseline) size
    const contentH = node.scrollHeight;
    const contentW = node.scrollWidth;
    const availW = node.clientWidth;

    // Height scale to fit line-clamp and width scale to avoid overflow
    const scaleH = allowedHeight > 0 ? allowedHeight / contentH : 1;
    const scaleW = availW > 0 ? availW / contentW : 1;

    return clampScale(Math.min(scaleH, scaleW, 1));
  }

  // Measure with a fixed desired line count override (used for product cards)
  function measureRequiredScaleFixedLines(el, desiredLines) {
    const node = el.querySelector('a') || el;
    if (!node || !node.clientWidth || el.offsetParent === null) return 1;

    const cs = window.getComputedStyle(node);
    const lh = parseFloat(cs.lineHeight) || (parseFloat(cs.fontSize) * 1.2) || 16 * 1.2;
    const allowedHeight = lh * (desiredLines || 1);

    const contentH = node.scrollHeight;
    const contentW = node.scrollWidth;
    const availW = node.clientWidth;

    const scaleH = allowedHeight > 0 ? allowedHeight / contentH : 1;
    const scaleW = availW > 0 ? availW / contentW : 1;

    return clampScale(Math.min(scaleH, scaleW, 1));
  }

  // Global product title scale management (persist per viewport bucket)
  function getViewportBucket() {
    const w = window.innerWidth || document.documentElement.clientWidth || 0;
    if (w >= 1200) return 'lg';
    if (w >= 750) return 'md';
    return 'sm';
  }

  function readStoredProductScale() {
    try {
      const key = PRODUCT_KEY_PREFIX + getViewportBucket();
      const v = localStorage.getItem(key);
      const num = v ? parseFloat(v) : NaN;
      if (isFinite(num) && num > 0 && num <= 1) return num;
    } catch (e) {
      // ignore storage errors
    }
    return 1;
  }

  function writeStoredProductScale(scale) {
    try {
      const key = PRODUCT_KEY_PREFIX + getViewportBucket();
      localStorage.setItem(key, String(scale));
    } catch (e) {
      // ignore storage errors
    }
  }

  function applyProductScaleToRoot(scale) {
    document.documentElement.style.setProperty('--product-card-title-scale', String(scale));
  }

  // Ensure currency (price) text uses a smaller size than product title, with a mobile exception.
  // Rule: mobile (sm) -> price offset = 1rem smaller; tablet/desktop (md/lg) -> 2rem smaller.
  function applyResponsivePriceOffset() {
    const bucket = getViewportBucket();
    const offset = bucket === 'sm' ? '1rem' : '2rem';
    document.documentElement.style.setProperty('--product-card-price-offset', offset);
  }

  function balanceProducts() {
    // Always apply stored scale first so pages without product lists still get uniform sizing
    const stored = readStoredProductScale();
    applyProductScaleToRoot(stored);

    const headings = Array.from(document.querySelectorAll('.product-card-wrapper .card__heading')).filter(
      (n) => n && n.offsetParent !== null
    );
    if (!headings.length) return; // nothing to measure on this page

    let minScale = 1;
    for (const h of headings) {
      const s = measureRequiredScaleFixedLines(h, 1); // enforce single-line for product titles
      if (s < minScale) minScale = s;
    }
    // Use the most constrained of stored vs computed to ensure global uniformity per bucket
    const effective = clampScale(Math.min(stored, minScale));
    if (effective !== stored) writeStoredProductScale(effective);
    applyProductScaleToRoot(effective);
  }

  function balanceContainer(container, selector) {
    if (!container) return;
    // Reset to baseline before measuring
    container.style.removeProperty('--collection-title-scale');

    const headings = Array.from(container.querySelectorAll(selector)).filter(
      (n) => n && n.offsetParent !== null
    );
    if (!headings.length) return;

    let minScale = 1;
    for (const h of headings) {
      const s = measureRequiredScale(h);
      if (s < minScale) minScale = s;
    }

    container.style.setProperty('--collection-title-scale', String(minScale));
  }

  function balanceAll() {
    // Keep currency sizing readable on small screens
    applyResponsivePriceOffset();
    // Global product card titles (catalog-wide uniformity per viewport bucket)
    balanceProducts();

    // Seasonal Collections / Categories using collection-list
    document.querySelectorAll('.collection-list-wrapper').forEach((c) => {
      cleanupStrayTokens(c);
      balanceContainer(c, '.collection-card-wrapper .card__heading');
    });
    // Categories variants using multicolumn
    document.querySelectorAll('.multicolumn').forEach((c) => {
      cleanupStrayTokens(c);
      balanceContainer(c, '.multicolumn-list h3');
    });
  }

  const rafBalance = () => window.requestAnimationFrame(balanceAll);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', rafBalance);
  } else {
    rafBalance();
  }

  // Recompute on resize; debounce to avoid thrash
  let resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(rafBalance, 150);
  });
})();
